import { useState, forwardRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BookOpen, Plus, Minus, ChevronDown, Bookmark, Check, Headphones } from "lucide-react";
import { SURAHS, TOTAL_JUZ, calculateQuranProgress, getSurah } from "@/data/quranData";
import { QuranPlayer } from "./QuranPlayer";

export interface QuranProgress {
  pages: number;
  juz: number;
  lastSurah?: number;
  lastAyah?: number;
  completedSurahs?: number[];
}

interface QuranTrackerProps {
  day: number;
  progress: Record<number, QuranProgress>;
  onUpdate: (day: number, data: QuranProgress) => void;
}

type ViewMode = "simple" | "detailed" | "player";

const DEFAULT_PROGRESS: QuranProgress = {
  pages: 0,
  juz: 0,
  lastSurah: 1,
  lastAyah: 1,
  completedSurahs: [],
};

export const QuranTracker = forwardRef<HTMLDivElement, QuranTrackerProps>(
  ({ day, progress, onUpdate }, ref) => {
    const [viewMode, setViewMode] = useState<ViewMode>("simple");
    const [showSurahPicker, setShowSurahPicker] = useState(false);
    const [selectedJuz, setSelectedJuz] = useState<number | null>(null);
    
    // Safely get today's progress with all required fields
    const rawProgress = progress[day];
    const todayProgress: QuranProgress = {
      ...DEFAULT_PROGRESS,
      ...rawProgress,
    };
    
    const totalPages = Object.values(progress).reduce((sum, p) => sum + (p?.pages || 0), 0);
    const completedSurahs = todayProgress.completedSurahs || [];
    
    // Calculate overall Quran completion
    const lastSurah = getSurah(todayProgress.lastSurah || 1);
    const overallProgress = lastSurah 
      ? calculateQuranProgress(todayProgress.lastSurah || 1, todayProgress.lastAyah || 1)
      : 0;

    const updatePages = (delta: number) => {
      const newPages = Math.max(0, (todayProgress.pages || 0) + delta);
      onUpdate(day, { ...todayProgress, pages: newPages });
    };

    const updateJuz = (delta: number) => {
      const newJuz = Math.max(0, Math.min(30, (todayProgress.juz || 0) + delta));
      onUpdate(day, { ...todayProgress, juz: newJuz });
    };

    const updateLastRead = (surahNumber: number, ayahNumber: number) => {
      onUpdate(day, { 
        ...todayProgress, 
        lastSurah: surahNumber, 
        lastAyah: ayahNumber 
      });
      setShowSurahPicker(false);
    };

    const toggleSurahCompletion = (surahNumber: number) => {
      const newCompleted = completedSurahs.includes(surahNumber)
        ? completedSurahs.filter(s => s !== surahNumber)
        : [...completedSurahs, surahNumber];
      onUpdate(day, { ...todayProgress, completedSurahs: newCompleted });
    };

    const juzArray = Array.from({ length: 30 }, (_, i) => i + 1);

    return (
      <div ref={ref} className="space-y-4">
        {/* Header with view toggle */}
        <div className="flex items-center justify-between">
          <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
            Day {day}
          </span>
          <div className="flex items-center gap-3">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setViewMode(viewMode === "player" ? "simple" : "player");
              }}
              className={`flex items-center gap-1 text-xs transition-colors ${
                viewMode === "player" ? "text-primary" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Headphones className="w-3.5 h-3.5" />
              Player
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setViewMode(viewMode === "detailed" ? "simple" : "detailed");
              }}
              className="text-xs text-primary hover:underline"
            >
              {viewMode === "simple" ? "Detailed" : viewMode === "detailed" ? "Simple" : "Track"}
            </button>
          </div>
        </div>

        {/* Player View */}
        {viewMode === "player" && (
          <QuranPlayer
            initialSurah={todayProgress.lastSurah || 1}
            initialAyah={todayProgress.lastAyah || 1}
            onAyahChange={(surah, ayah) => {
              onUpdate(day, { ...todayProgress, lastSurah: surah, lastAyah: ayah });
            }}
          />
        )}

        {/* Simple View - Pages & Juz counters */}
        {viewMode === "simple" && (
          <div className="grid grid-cols-2 gap-4">
            {/* Pages counter */}
            <div className="bg-background border border-border rounded-sm p-4 space-y-2">
              <div className="flex items-center gap-2 text-muted-foreground">
                <BookOpen className="w-4 h-4" />
                <span className="text-xs uppercase tracking-wider">Pages Today</span>
              </div>
              <div className="flex items-center justify-between">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    updatePages(-1);
                  }}
                  className="w-8 h-8 rounded-full bg-cream flex items-center justify-center hover:bg-accent transition-colors active:scale-90"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="text-2xl font-display text-ink">{todayProgress.pages || 0}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    updatePages(1);
                  }}
                  className="w-8 h-8 rounded-full bg-cream flex items-center justify-center hover:bg-accent transition-colors active:scale-90"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs text-center text-muted-foreground">
                Total: {totalPages} pages
              </p>
            </div>

            {/* Juz counter */}
            <div className="bg-background border border-border rounded-sm p-4 space-y-2">
              <div className="flex items-center gap-2 text-muted-foreground">
                <span className="text-xs uppercase tracking-wider">Juz Completed</span>
              </div>
              <div className="flex items-center justify-between">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    updateJuz(-1);
                  }}
                  className="w-8 h-8 rounded-full bg-cream flex items-center justify-center hover:bg-accent transition-colors active:scale-90"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="text-2xl font-display text-ink">{todayProgress.juz || 0}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    updateJuz(1);
                  }}
                  className="w-8 h-8 rounded-full bg-cream flex items-center justify-center hover:bg-accent transition-colors active:scale-90"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs text-center text-muted-foreground">
                of {TOTAL_JUZ} juz
              </p>
            </div>
          </div>
        )}

        {/* Detailed View - Surah & Ayah tracking */}
        {viewMode === "detailed" && (
          <div className="space-y-4">
            {/* Last Read Position */}
            <div className="bg-background border border-border rounded-sm p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Bookmark className="w-4 h-4" />
                  <span className="text-xs uppercase tracking-wider">Last Read</span>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowSurahPicker(!showSurahPicker);
                  }}
                  className="text-xs text-primary hover:underline flex items-center gap-1"
                >
                  Update
                  <ChevronDown className={`w-3 h-3 transition-transform ${showSurahPicker ? 'rotate-180' : ''}`} />
                </button>
              </div>
              
              <div className="text-center py-2">
                <p className="text-lg font-display text-foreground">
                  {lastSurah?.name || "Al-Fatihah"}
                </p>
                <p className="text-sm text-muted-foreground">
                  {lastSurah?.englishName} • Ayah {todayProgress.lastAyah || 1} of {lastSurah?.ayahCount || 7}
                </p>
                <p className="text-xs text-primary mt-1">
                  {overallProgress.toFixed(1)}% of Quran
                </p>
              </div>

              {/* Surah Picker */}
              <AnimatePresence>
                {showSurahPicker && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="pt-4 border-t border-border mt-4">
                      {/* Juz Filter */}
                      <div className="mb-3">
                        <p className="text-xs text-muted-foreground mb-2">Filter by Juz</p>
                        <div className="flex flex-wrap gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedJuz(null);
                            }}
                            className={`px-2 py-1 text-xs rounded ${
                              selectedJuz === null 
                                ? 'bg-primary text-primary-foreground' 
                                : 'bg-muted hover:bg-muted/80'
                            }`}
                          >
                            All
                          </button>
                          {juzArray.map(j => (
                            <button
                              key={j}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedJuz(j);
                              }}
                              className={`px-2 py-1 text-xs rounded ${
                                selectedJuz === j 
                                  ? 'bg-primary text-primary-foreground' 
                                  : 'bg-muted hover:bg-muted/80'
                              }`}
                            >
                              {j}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Surah List */}
                      <div className="max-h-48 overflow-y-auto space-y-1">
                        {SURAHS
                          .filter(s => selectedJuz === null || s.juz.includes(selectedJuz))
                          .map(surah => (
                            <button
                              key={surah.number}
                              onClick={(e) => {
                                e.stopPropagation();
                                updateLastRead(surah.number, 1);
                              }}
                              className={`w-full text-left px-3 py-2 rounded text-sm hover:bg-muted transition-colors flex items-center justify-between ${
                                todayProgress.lastSurah === surah.number ? 'bg-primary/10' : ''
                              }`}
                            >
                              <span>
                                <span className="text-muted-foreground mr-2">{surah.number}.</span>
                                {surah.name}
                                <span className="text-muted-foreground ml-2 text-xs">
                                  ({surah.englishName})
                                </span>
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {surah.ayahCount} ayahs
                              </span>
                            </button>
                          ))}
                      </div>

                      {/* Ayah Input */}
                      {todayProgress.lastSurah && (
                        <div className="mt-3 pt-3 border-t border-border">
                          <label className="text-xs text-muted-foreground block mb-2">
                            Ayah in {getSurah(todayProgress.lastSurah)?.name}
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min={1}
                              max={getSurah(todayProgress.lastSurah)?.ayahCount || 1}
                              value={todayProgress.lastAyah || 1}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                const val = parseInt(e.target.value) || 1;
                                const max = getSurah(todayProgress.lastSurah!)?.ayahCount || 1;
                                updateLastRead(todayProgress.lastSurah!, Math.min(Math.max(1, val), max));
                              }}
                              className="flex-1 px-3 py-2 text-sm border border-border rounded bg-background"
                            />
                            <span className="text-xs text-muted-foreground">
                              / {getSurah(todayProgress.lastSurah)?.ayahCount}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Quick Surah Completion Toggle */}
            <div className="bg-background border border-border rounded-sm p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-3">
                <Check className="w-4 h-4" />
                <span className="text-xs uppercase tracking-wider">Surahs Read Today</span>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                Tap to mark surahs you've completed reading today
              </p>
              <div className="flex flex-wrap gap-1">
                {/* Show last 10 surahs (commonly recited short surahs) */}
                {SURAHS.slice(-10).map(surah => (
                  <button
                    key={surah.number}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSurahCompletion(surah.number);
                    }}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      completedSurahs.includes(surah.number)
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted hover:bg-muted/80'
                    }`}
                  >
                    {surah.number}. {surah.name.split('-')[1] || surah.name}
                  </button>
                ))}
              </div>
              {completedSurahs.length > 0 && (
                <p className="text-xs text-primary mt-2">
                  {completedSurahs.length} surah{completedSurahs.length !== 1 ? 's' : ''} marked as read
                </p>
              )}
            </div>

            {/* Pages counter in detailed view too */}
            <div className="bg-background border border-border rounded-sm p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <BookOpen className="w-4 h-4" />
                <span className="text-xs uppercase tracking-wider">Pages Read Today</span>
              </div>
              <div className="flex items-center justify-center gap-4">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    updatePages(-1);
                  }}
                  className="w-8 h-8 rounded-full bg-cream flex items-center justify-center hover:bg-accent transition-colors active:scale-90"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="text-2xl font-display text-ink">{todayProgress.pages || 0}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    updatePages(1);
                  }}
                  className="w-8 h-8 rounded-full bg-cream flex items-center justify-center hover:bg-accent transition-colors active:scale-90"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Progress bar for juz */}
        <div className="space-y-1">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Juz Progress</span>
            <span>{todayProgress.juz || 0}/{TOTAL_JUZ}</span>
          </div>
          <div className="h-1.5 bg-border rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gold rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${((todayProgress.juz || 0) / TOTAL_JUZ) * 100}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>
      </div>
    );
  }
);

QuranTracker.displayName = "QuranTracker";
